import { formatCurrency } from '@/features/promotions/money'
import type { Event } from '@/lib/api/types'

/** The currencies the card readers charge in (backend SDK_READER_CURRENCIES). */
export const CARD_CURRENCIES = ['USD']

/**
 * Whether the card readers charge the event's currency. Until they do, the
 * event's card payments switch is shown but can't be turned on.
 */
export function cardsChargeCurrency(currency: string | null | undefined) {
  return CARD_CURRENCIES.includes((currency || '').toUpperCase())
}

const CURRENCY_EVENTS: Record<string, string> = { CAD: 'Canadian events', USD: 'US events' }

/**
 * "Coming soon to Canadian events": the notice on an event whose currency the
 * readers do not charge yet (Tap to Pay is USD until Finix confirms CAD).
 */
export function cardsComingSoonText(currency: string | null | undefined) {
  const code = (currency || '').toUpperCase()
  const where = CURRENCY_EVENTS[code] ?? (code ? `events charging ${code}` : 'this event')
  return `Coming soon to ${where}`
}

/** Whether the event can take cards at all: a Finix event with a merchant. */
export function eventTakesCards(event: Pick<Event, 'payment_gateway' | 'finix_merchant_account_id'>) {
  return event.payment_gateway === 'finix' && !!event.finix_merchant_account_id
}

/** "2.9% + $0.30", "None" for nothing. The percentage is a fraction. */
export function feeLabel(percentage: number | null | undefined, fixed: number | null | undefined, currency?: string) {
  const pct = Number(percentage) || 0
  const flat = Number(fixed) || 0
  if (pct === 0 && flat === 0) return 'None'
  // Two decimals at most: 0.029 shows as 2.9%, not 2.9000000000000004%.
  return `${Math.round(pct * 10000) / 100}% + ${formatCurrency(flat, currency)}`
}

/** The card fee a tap carries at the event, as a sentence ending. */
export function eventCardFeeText(event: Pick<Event, 'variable_fee_percentage' | 'fixed_fee_amount' | 'currency'>) {
  const fee = feeLabel(event.variable_fee_percentage, event.fixed_fee_amount, event.currency)
  return fee === 'None' ? 'none (the event has no processing fee)' : `${fee} (the event’s processing fee)`
}
